import React, { useEffect, useState, useRef } from 'react';
import { Button, ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { Registerer, SessionState, UserAgent } from 'sip.js';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toZonedTime } from 'date-fns-tz';
import { startOfDay } from 'date-fns';
import * as Icon from 'react-feather';

// COMPONENT IMPORTS
import config from '../../config';
import Timer from '../../utils/Timer';
import { socket } from '../../config/Socket';
import DtmfScreen from './dialScreens/DtmfScreen';
import DialPadScreen from './dialScreens/DialPadScreen';
import TransferScreen from './dialScreens/TransferScreen';
import ringtoneMp3 from '../../assets/sound/ringtone.mp3';
import { useCallingData } from '../Context/CallingContext';
import DefaultLiveCall from './dialScreens/DefaultLiveCall';
import ConferenceScreen from './dialScreens/ConferenceScreen';
import UserTransferScreen from './dialScreens/UserTransferScreen';
import IncomingCallScreen from './dialScreens/IncomingCallScreen';
import AgentTransferScreen from './dialScreens/AgentTransferScreen';
import ConferenceLiveScreen from './dialScreens/ConferenceLiveScreen';
import TransferHangupScreen from './dialScreens/TransferHangupScreen';
import { formatPayloadDate, getCurrentDateTime } from '../../utils/common';

// APIS IMPORTS
import { setCallingModalOpen, setSidebarType, useTimezone } from '../../store/slices/commonSlice';
import { useUserDetails } from '../../store/slices/authSlice';
import {
  onSetCallStatus,
  onSetCallType,
  setBreakDetails,
  setCallDialSec,
  setCallEnd,
  setCallId,
  setCallNumber,
  setCurrentCallDetail,
  setIsDialNumber,
  setIsReDial,
  setScreenType,
  setSelectBreakType,
  useBreakDetails,
  useBreakType,
  useCallId,
  useCallingStatus,
  useCurrentCallDetail,
  useScreenType,
  useIsDialNumber,
  useDialSec,
  useIsReDial,
  useCallEnd,
  useHoldTime,
  setHoldTime,
  setHoldStartTime,
  useHoldStartTime,
  setSipStatus,
  useCallType,
  useSipStatus,
  useConferenceDetail,
  setConferenceDetail
} from '../../store/slices/dialSlice';
import {
  breaksListGet,
  crmInfoLog,
  crmInformation,
  followUpAdd,
  handleConference,
  manualCall,
  manualDialing,
  pauseModeUpdate,
  progressiveCall,
  // removeCrmDetails,
  selectBreak,
  setIdleCall,
  setPauseMode,
  setUnregisterCall,
  updateCrmInformation,
  useBreaksList,
  useCrmDetails,
  usePauseMode
} from '../../store/slices/callingSlice';

const CallingSidebar = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const timezone = useTimezone();
  const today = toZonedTime(new Date(), timezone);
  const {
    informationData,
    dispositionsData,
    fieldsData,
    followUp,
    isPauseMode,
    setFollowUp,
    setIsPauseMode,
    handleTimeReset,
    setMutedConference
  } = useCallingData();
  const pauseMode = usePauseMode();
  const user = useUserDetails();
  const callStatus = useCallingStatus();
  const callId = useCallId();
  const breaksList = useBreaksList();
  const breakType = useBreakType();
  const screenType = useScreenType();
  const isDialNumber = useIsDialNumber();
  const breakDetails = useBreakDetails();
  const CurrentCallDetail = useCurrentCallDetail();
  const crmDetails = useCrmDetails();
  const dialSec = useDialSec();
  const isReDial = useIsReDial();
  const callEnd = useCallEnd();
  const holdTime = useHoldTime();
  const holdStartTime = useHoldStartTime();
  const callType = useCallType();
  const sipStatus = useSipStatus();
  const conferenceDetail = useConferenceDetail();

  const [isOpen, setIsOpen] = useState(false);
  const [isConVoxOpen, setIsConVoxOpen] = useState(false);
  const [number, setNumber] = useState('');
  const [duration, setDuration] = useState(0);
  const [wrapsec, setWrapsec] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const intervalRef = useRef(null);
  const incomingInvitationRef = useRef(null);
  const ringtoneRef = useRef(null);

  let userAgent;
  let registerer;
  const remoteStream = new MediaStream();

  const connectSIP = async () => {
    try {
      const sipUri = UserAgent.makeURI(
        `sip:${user.username}@${user.domain}:${process.env.REACT_APP_SIP_PORT}`
      );
      if (!sipUri) {
        console.error('Invalid SIP URI');
        return;
      }

      const userAgentOptions = {
        uri: sipUri,
        authorizationPassword: user?.password,
        authorizationUsername: user?.username,
        transportOptions: {
          server: config.WSS_URL
        }
      };
      dispatch(setSipStatus('Connecting'));
      console.log('Creating UserAgent...');
      userAgent = new UserAgent(userAgentOptions);

      console.log('Starting UserAgent...');
      await userAgent.start();

      console.log('Registering UserAgent...');
      const registerer = new Registerer(userAgent);
      await registerer.register();

      console.log('UserAgent registered and ready for calls.');
      dispatch(setSipStatus('Register'));
      userAgent.delegate = {
        onInvite: async (invitation) => {
          console.log('Incoming call...',process.env.REACT_APP_INCOMING_SCREEN);
          incomingInvitationRef.current = invitation;
          if (process.env.REACT_APP_INCOMING_SCREEN === 'true') {
          console.log('IF REACT_APP_INCOMING_SCREEN',process.env.REACT_APP_INCOMING_SCREEN);
            playRingtone();

            invitation.stateChange.addListener((state) => {
              if (state === SessionState.Terminated) {
                stopRingtone();
                incomingInvitationRef.current = null;
              }
            });
          } else {
          console.log('ELSE REACT_APP_INCOMING_SCREEN',process.env.REACT_APP_INCOMING_SCREEN);
            try {
              await invitation.accept({
                sessionDescriptionHandlerOptions: {
                  constraints: {
                    audio: true,
                    video: false
                  }
                }
              });
              setupRemoteMedia(invitation);
              console.log('Call answered!');
            } catch (err) {
              console.error('Error answering call:', err);
            }
          }
        }
      };
    } catch (error) {
      console.error('SIP Connection Error:', error);
    }
  };

  const answerCall = async () => {
    const invitation = incomingInvitationRef.current;
    if (!invitation) return;
    stopRingtone();

    try {
      await invitation.accept({
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: true,
            video: false
          }
        }
      });
      setupRemoteMedia(invitation);
      console.log('Incoming call answered!');
      incomingInvitationRef.current = null;
    } catch (err) {
      incomingInvitationRef.current = null;
      console.error('Error answering Incoming call:', err);
    }
  };

  const registerUA = async () => {
    try {
      registerer = new Registerer(userAgent);
      registerer
        .register()
        .then(async () => {
          dispatch(setSipStatus('Register'));
          console.log('UA Successfully registered ...');
        })
        .catch(async (error) => {
          console.error('Failed to send REGISTER ==> ', error);
        });
    } catch (error) {
      console.error('CATCH FOUND -  registerUA ==> ', error);
    }
  };

  const reconnectUA = async () => {
    try {
      userAgent
        .reconnect()
        .then(async () => {
          dispatch(setSipStatus('Connecting'));
          await registerUA();
        })
        .catch(async (error) => {
          console.error('UA Reconnection error ==> ', error);
        });
    } catch (error) {
      console.error('CATCH FOUND -  reconnectUA ==> ', error);
    }
  };

  const setupRemoteMedia = (mediaSession) => {
    const mediaElement = document.getElementById('mediaElement');
    try {
      mediaSession.sessionDescriptionHandler.peerConnection.getReceivers().forEach((receiver) => {
        if (receiver.track) {
          remoteStream.addTrack(receiver.track);
          mediaElement.srcObject = remoteStream;
          mediaElement.play();
        }
      });
    } catch (error) {
      console.log('Media audio session error - ', error);
    }
  };

  const playRingtone = () => {
    ringtoneRef.current
      ?.play()
      .catch((err) => console.warn('Ringtone blocked until user interaction', err));
  };

  const stopRingtone = () => {
    if (!ringtoneRef.current) return;
    ringtoneRef.current.pause();
    ringtoneRef.current.currentTime = 0;
  };

  useEffect(() => {
    ringtoneRef.current = new Audio(ringtoneMp3);
    ringtoneRef.current.loop = true;

    return () => {
      ringtoneRef.current?.pause();
      ringtoneRef.current = null;
    };
  }, []);

  useEffect(() => {
    const handleOnline = async () => {
      if (Number(user.web_phone) === 0) {
        reconnectUA();
      }
    };

    const handleOffline = () => {
      if (Number(user.web_phone) === 0) {
        dispatch(setSipStatus('Un-Register'));
        alert('Please check your connection');
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [user.web_phone]);

  useEffect(() => {
    if (Number(user.web_phone) === 0 && !breakDetails) {
      Object.defineProperty(window, 'isSecureContext', { get: () => true });

      if (!userAgent) {
        connectSIP();
      } else {
        reconnectUA();
      }
    } else {
      dispatch(setSipStatus('Un-Register'));
      console.log('SIP registration is disabled due to web_phone flag.');
    }

    return () => {
      if (userAgent) {
        dispatch(setSipStatus('Un-Register'));
        console.log('Stopping UserAgent...');
        userAgent.stop();
      }
    };
  }, [user.web_phone, breakDetails]);

  useEffect(() => {
    let timerInterval;
    if (callStatus === 'WRAPUP') {
      timerInterval = setInterval(() => {
        setWrapsec((prevTime) => prevTime + 1);
      }, 1000);
    }
    return () => {
      clearInterval(timerInterval);
    };
  }, [callStatus]);

  useEffect(() => {
    const autoWrapUpZero = String(user?.process_details?.autoWrapup) === '0';
    const autoWrapUp = Number(user?.process_details?.autoWrapup);
    if (
      ((!autoWrapUpZero && wrapsec > autoWrapUp) || callEnd) &&
      !isLoading &&
      location.pathname !== '/crm' &&
      dialSec !== null
    ) {
      updateCrm();
    }
  }, [wrapsec, location.pathname, callEnd]);

  const onPauseModeChange = async (pause_mode) => {
    try {
      const payload = { user_id: user?.id, pause_mode: pause_mode };
      const response = await dispatch(pauseModeUpdate(payload)).unwrap();
      if (response && response?.success) {
        dispatch(setPauseMode(payload?.pause_mode));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleConferenceMode = async () => {
    try {
      const payload = {
        conf_id: 'all',
        type: 'hangup',
        conf_name: conferenceDetail?.conference_name
      };
      await dispatch(handleConference(payload)).unwrap();
      dispatch(setConferenceDetail(null));
      setMutedConference([]);
    } catch (error) {
      console.log('error', error);
    }
  };

  const updateCrm = async () => {
    setIsLoading(true);
    if (conferenceDetail && conferenceDetail?.details?.length > 0) {
      handleConferenceMode();
    }
    try {
      const autoWrapUpZero = String(user?.process_details?.autoWrapup) === '0';
      const autoWrapup = Number(user?.process_details?.autoWrapup);

      let hold_time = 0;

      if (holdStartTime !== null) {
        const holdDuration = Math.floor((Date.now() - holdStartTime) / 1000);
        hold_time = holdTime + holdDuration;
      } else {
        hold_time = holdTime;
      }

      const payload = {
        data: [
          {
            crmDetails: { ...informationData }
          },
          {
            fields: fieldsData?.fields ? fieldsData?.fields : []
          },
          {
            dispositions: dispositionsData?.dispositioncode
              ? { ...dispositionsData }
              : { dispositioncode: 'AUTOWRAP' }
          },
          {
            unique_id: crmDetails?.[4]?.unique_id,
            dialsec: callType === 'predictive' || callType === 'incoming' ? 0 : dialSec,
            hold_time: hold_time,
            wrapsec: wrapsec,
            auto_wrapup: !autoWrapUpZero ? autoWrapup <= wrapsec : false
          }
        ]
      };
      if (followUp?.isFollowUp === 'yes') {
        dispatch(
          followUpAdd({
            phone_number: crmDetails?.[1]?.crmDetails?.primaryNumber,
            crm_id: crmDetails?.[1]?.crmDetails?.Crms_id,
            date_and_time: formatPayloadDate(followUp?.date_and_time)
          })
        ).unwrap();
      }
      if (isPauseMode) {
        onPauseModeChange(1);
      }
      const data_message = {
        user_id: user.user_id,
        payload: payload,
        message: 'update Crm Information'
      };
      getLogDetails(data_message);
      const response = await dispatch(updateCrmInformation(payload)).unwrap();
      if (response) {
        const responseTimer = setTimeout(() => {
          if (isReDial) {
            dispatch(setIsDialNumber(crmDetails?.[1]?.crmDetails?.primaryNumber));
          }
          setWrapsec(0);
          dispatch(setCallId(null));
          dispatch(setCallingModalOpen(user?.manualcall ? true : false));
          dispatch(setCallDialSec(null));
          dispatch(setCurrentCallDetail(null));
          dispatch(setSidebarType('main'));
          dispatch(setCallEnd(false));
          handleTimeReset();
          dispatch(onSetCallStatus(null));
          dispatch(onSetCallType(null));
          if (location.pathname !== '/chat') {
            navigate('/dashboard');
          }
          setIsLoading(false);
          dispatch(setHoldTime(0));
          dispatch(setHoldStartTime(null));
          setFollowUp({
            date_and_time: startOfDay(today),
            isFollowUp: 'no'
          });
          setIsPauseMode(false);
        }, 500);

        // const removeTimer = setTimeout(() => {
        //   dispatch(removeCrmDetails());
        // }, 600);

        return () => {
          clearTimeout(responseTimer);
          // clearTimeout(removeTimer);
        };
      }
    } catch (error) {
      setIsLoading(false);
      const data_message = {
        user_id: user.user_id,
        payload: error?.message,
        message: 'update Crm Information error'
      };
      getLogDetails(data_message);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (callStatus === 'WRAPUP' || !callStatus) {
      dispatch(setScreenType(''));
      setNumber('');
      dispatch(setCallNumber(''));
      setWrapsec(0);
    }
  }, [callStatus, breakDetails]);

  useEffect(() => {
    socket.emit('identify', { user_id: user.user_id, type: user?.type });

    const handleServerRestart = ({ message }) => {
      console.log('server_restart-------->', message);
      socket.emit('identify', { user_id: user.user_id, type: user?.type });
    };

    const handleMessage = ({ message }) => {
      console.log('socket msg-->', message);
      console.log('socket id-->', socket.id);
      const data_message = {
        user_id: user.user_id,
        message: 'socket msg',
        'socket.id': socket.id,
        message_data: message
      };
      getLogDetails(data_message);
      if (!!breakDetails || (sipStatus === 'Un-Register' && Number(user.web_phone) === 0)) {
        return false;
      }
      if ((!!breakDetails || callStatus) && message?.event_type === 'EXTENSION_RINGING') {
        return false;
      }
      dispatch(onSetCallType(message?.call_type));
      if (message?.call_type === 'HANGUP') {
        dispatch(setCallId(message?.origination_uuid));
        dispatch(setCurrentCallDetail(message));
      }
      if (
        (message?.call_type === 'predictive' ||
          message?.call_type === 'incoming' ||
          message?.call_type === 'voicebroadcast') &&
        (message?.event_type === 'ANSWER' || message?.event_type === 'EXTENSION_RINGING')
      ) {
        dispatch(setCallId(message?.origination_uuid));
        dispatch(setCallingModalOpen(true));
        dispatch(setCurrentCallDetail(message));
        getCrmDetails(message);
        dispatch(setCallDialSec(0));
        dispatch(
          setScreenType(
            message?.call_type === 'incoming' &&
              message?.event_type === 'EXTENSION_RINGING' &&
              process.env.REACT_APP_INCOMING_SCREEN === 'true'
              ? 'INCOMING'
              : 'LIVE_CALL'
          )
        );
      }
      const callType =
        message?.event_type === 'EXTENSION_RINGING'
          ? 'RINGING'
          : message?.event_type === 'DIALING'
            ? 'DIALING'
            : message?.event_type === 'ANSWER'
              ? 'ON CALL'
              : message?.event_type === 'HANGUP'
                ? 'WRAPUP'
                : null;
      handleTimeReset();
      dispatch(onSetCallStatus(callType));
      if (callType) {
        dispatch(setSelectBreakType(null));
      }
    };

    socket.on('connect', () => {
      console.log('Socket connected: ', socket.id);
      socket.emit('identify', { user_id: user.user_id, type: user?.type });
    });

    socket.on('reconnect', (attemptNumber) => {
      console.log('Socket reconnected after attempt ', attemptNumber);
      socket.emit('identify', { user_id: user.user_id, type: user?.type });
    });

    socket.on('server_restart', handleServerRestart);
    socket.on('sendMessage', handleMessage);

    return () => {
      socket.off('server_restart', handleServerRestart);
      socket.off('sendMessage', handleMessage);
      socket.off('connect');
      socket.off('reconnect');
    };
  }, [user, callStatus, breakDetails, sipStatus]);

  const getLogDetails = async (data) => {
    try {
      await dispatch(crmInfoLog(data)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const onProgressiveCall = async () => {
    try {
      const response = await dispatch(progressiveCall()).unwrap();
      if (response && response?.data) {
        dispatch(setCallId(response?.data?.origination_uuid));
        dispatch(setCurrentCallDetail(response?.data));
        getCrmDetails(response?.data);
        dispatch(setScreenType('LIVE_CALL'));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (
      user &&
      user?.process_details &&
      (user?.process_details?.type === 'OutboundAndProgressive' ||
        user?.process_details?.type === 'BlendedAndProgressive') &&
      !callStatus &&
      !breakType
    ) {
      if (Number(user.web_phone) === 0 && sipStatus === 'Register') {
        const apiRefresh =
          user?.process_details?.ProgressiveDialingSec === 0
            ? 5
            : user?.process_details?.ProgressiveDialingSec;

        const startAPICallInterval = () => {
          if (!intervalRef.current) {
            intervalRef.current = setInterval(() => {
              onProgressiveCall();
            }, apiRefresh * 1000);
          }
        };

        startAPICallInterval();

        return () => {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        };
      }

      if (Number(user.web_phone) !== 0) {
        const apiRefresh =
          user?.process_details?.ProgressiveDialingSec === 0
            ? 5
            : user?.process_details?.ProgressiveDialingSec;

        const startAPICallInterval = () => {
          if (!intervalRef.current) {
            intervalRef.current = setInterval(() => {
              onProgressiveCall();
            }, apiRefresh * 1000);
          }
        };

        startAPICallInterval();

        return () => {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        };
      }
    }
  }, [user, callStatus, breakType, sipStatus]);

  const manualDialingOn = async () => {
    try {
      await dispatch(manualDialing()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (breakType && breakType.name === 'OUTGOING') {
      manualDialingOn();
    }
  }, [breakType]);

  const getBreaksList = async () => {
    try {
      const payload = { process_id: user?.process_details?.process_id };
      await dispatch(breaksListGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    getBreaksList();
  }, [user]);

  const onUnregisterCall = async () => {
    try {
      await dispatch(setUnregisterCall()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const onSelectBreak = async (data, type) => {
    try {
      if (type === 'in') {
        await onUnregisterCall();
      }
      const payload = {
        break_id: data?.id,
        break_name: data?.name,
        type: type
      };
      await dispatch(selectBreak(payload)).unwrap();
      dispatch(setBreakDetails(type === 'in' ? payload : null));
      dispatch(
        setSelectBreakType(
          type === 'in' ? { id: data?.id, name: data?.name, color: 'warning' } : null
        )
      );
      handleTimeReset();
      if (pauseMode === 0) {
        onPauseModeChange(1);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isDialNumber) {
      dialNumber(isDialNumber);
    }
  }, [isDialNumber]);

  const dialNumber = async (number) => {
    setNumber(number);
    dispatch(setCallNumber(number));
    dispatch(setIsReDial(false));
    dispatch(setIsDialNumber(null));
    try {
      const payload = {
        destination_number: number
      };
      const response = await dispatch(manualCall(payload)).unwrap();
      if (response && response?.data) {
        dispatch(setCallDialSec(0));
        dispatch(setCallId(response?.data?.origination_uuid));
        dispatch(setCurrentCallDetail(response?.data));
        getCrmDetails(response?.data);
        dispatch(setScreenType('LIVE_CALL'));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const getCrmDetails = async (data) => {
    try {
      const payload = {
        crm_id: data?.crm_id,
        unique_id: data?.unique_id
      };
      const response = await dispatch(crmInformation(payload)).unwrap();
      const data_message = {
        user_id: user.user_id,
        message: 'try to fetch CRM',
        'socket.id': socket.id,
        message_data: response
      };
      getLogDetails(data_message);
      if (response) {
        dispatch(setSidebarType('CRM'));
        navigate('/crm');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (!crmDetails && CurrentCallDetail && CurrentCallDetail?.unique_id) {
      getCrmDetails(CurrentCallDetail);
    }
  }, [crmDetails, callId]);

  const onIdleSet = async () => {
    try {
      await dispatch(setIdleCall()).unwrap();
      setWrapsec(0);
      dispatch(setCallId(null));
      dispatch(setCurrentCallDetail(null));
      dispatch(setCallingModalOpen(user?.manualcall ? true : false));
      dispatch(setCallDialSec(null));
      dispatch(setSidebarType('main'));
      dispatch(setCallEnd(false));
      handleTimeReset();
      dispatch(onSetCallStatus(null));
      dispatch(onSetCallType(null));
      dispatch(setHoldTime(0));
      dispatch(setHoldStartTime(null));
      incomingInvitationRef.current = null;
      if (location.pathname !== '/chat') {
        navigate('/dashboard');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (callStatus === 'WRAPUP' && wrapsec > 60) {
      onIdleSet();
    }
  }, [callStatus, wrapsec]);

  const renderScreen = () => {
    switch (screenType) {
      case 'LIVE_CALL':
        return <DefaultLiveCall setNumber={setNumber} number={number} />;

      case 'INCOMING':
        return (
          <IncomingCallScreen
            setNumber={setNumber}
            number={number}
            answerCall={answerCall}
            stopRingtone={stopRingtone}
            incomingInvitationRef={incomingInvitationRef}
          />
        );

      case 'DTMF':
        return <DtmfScreen />;

      case 'TRANSFER':
        return <TransferScreen number={number} />;

      case 'AGENT_TRANSFER':
        return <AgentTransferScreen number={number} />;

      case 'ATTENDED_TRANSFER':
        return <UserTransferScreen number={number} />;

      case 'BLIND_TRANSFER':
        return <UserTransferScreen number={number} />;

      case 'TRANSFER_HANGUP':
        return <TransferHangupScreen number={number} />;

      case 'CONFERENCE':
        return <ConferenceScreen number={number} />;

      case 'CONFERENCE_LIVE':
        return <ConferenceLiveScreen number={number} setNumber={setNumber} />;

      default:
        return callStatus === 'WRAPUP' ? (
          <div className="input-container">
            <div className="d-flex flex-column gap-2 justify-content-center align-items-center">
              <div
                className="bg-violet rounded-circle d-flex justify-content-center align-items-center cursor-pointer"
                style={{ height: '70px', width: '70px' }}
                onClick={() => {
                  dispatch(setIsReDial(true));
                  socket.emit('identify-redial', { user_id: user.user_id, type: user?.type });
                  dispatch(setCallEnd(true));
                  setNumber('');
                  dispatch(setCallNumber(''));
                  dispatch(setScreenType(null));
                }}>
                <Icon.PhoneForwarded className="text-white" size={30} />
              </div>
              <div className="text-white fs-5 fw-bold">{t('redial')}</div>
            </div>
          </div>
        ) : (
          <DialPadScreen
            number={number}
            setNumber={setNumber}
            setDuration={handleTimeReset}
            duration={duration}
            onIdleSet={onIdleSet}
          />
        );
    }
  };

  const renderCallStatus = () => {
    const callStatusClasses = {
      WRAPUP: 'bg-violet',
      RINGING: 'bg-blue',
      DIALING: 'bg-purple',
      'ON CALL': 'bg-green'
    };

    const statusClass =
      callStatusClasses[callStatus] || (breakType?.color ? `bg-${breakType.color}` : 'bg-info');
    return (
      <div className={`${statusClass} py-1 w-100 text-center text-white rounded-2 idle-time`}>
        <span className="text-uppercase">
          {callStatus
            ? t(callStatus)
            : (breakType?.name && breakType?.name === 'OUTGOING'
                ? t('OUTGOING')
                : breakType?.name) || t('IDLE')}
        </span>{' '}
        <span>
          <Timer duration={duration} setDuration={setDuration} />
        </span>
      </div>
    );
  };

  const renderBreakStatus = () => {
    if (callStatus && callStatus !== 'WRAPUP') {
      return (
        <div className={`px-4 break-container ${!user?.manualcall && !callId && 'pt-0'}`}>
          <Button
            caret
            size="sm"
            className="ready-btn w-100"
            onClick={() => {
              getCrmDetails(CurrentCallDetail);
            }}>
            {t('refetchDetails')}
          </Button>
        </div>
      );
    }
    if (callStatus === 'WRAPUP') {
      return (
        <div className={`px-4 break-container ${!user?.manualcall && !callId && 'pt-0'}`}>
          <Button
            caret
            size="sm"
            className="ready-btn w-100"
            disabled={location.pathname === '/crm'}
            onClick={() => {
              onIdleSet();
            }}>
            {t('setIdle')}
          </Button>
        </div>
      );
    }
    if ((breakType && breakType?.name === 'OUTGOING') || callStatus !== null) {
      return false;
    }
    return (
      <div className={`px-4 break-container ${!user?.manualcall && !callId && 'pt-0'}`}>
        {breakType ? (
          <Button
            caret
            size="sm"
            className="ready-btn w-100"
            onClick={() => {
              onSelectBreak(breakType, 'out');
            }}>
            {t('ready')}
          </Button>
        ) : (
          <ButtonDropdown
            className="break-button-dropdown"
            isOpen={isOpen}
            toggle={() => setIsOpen(!isOpen)}>
            <DropdownToggle className="break-button" caret size="sm">
              {t('break')}
            </DropdownToggle>
            <DropdownMenu>
              {breaksList.map((val, index) => {
                return (
                  <DropdownItem
                    key={index}
                    onClick={() => {
                      onSelectBreak(val, 'in');
                    }}>
                    {val?.name}
                  </DropdownItem>
                );
              })}
            </DropdownMenu>
          </ButtonDropdown>
        )}
      </div>
    );
  };

  if (!user?.manualcall && !callId) {
    return (
      <div className="predictive-container">
        <div className="pb-2 text-center text-light fs-6">{getCurrentDateTime(timezone)}</div>
        <div className="px-4">
          <ButtonDropdown
            className="break-button-dropdown"
            isOpen={isConVoxOpen}
            toggle={() => setIsConVoxOpen(!isConVoxOpen)}>
            <DropdownToggle className="break-button" caret size="sm">
              {user?.process_details?.name}
            </DropdownToggle>
            <DropdownMenu>{user?.process_details?.name}</DropdownMenu>
          </ButtonDropdown>
        </div>
        <div className="px-4 py-2">{renderCallStatus()}</div>
        {renderBreakStatus()}
      </div>
    );
  }

  return (
    <div className="calling-sidebar p-3 position-relative">
      <div
        className="position-absolute bg-primary shadow-sm rounded-5 top-50 border-white text-white d-flex justify-content-center align-items-center callpase-icon"
        onClick={() => dispatch(setCallingModalOpen(false))}>
        <Icon.ChevronRight />
      </div>
      <div className="shadow-sm dail-page-container rounded-4 py-3">
        <div className="pb-2 text-center text-light fs-6">{getCurrentDateTime(timezone)}</div>
        <div className="px-4">
          <ButtonDropdown
            className="break-button-dropdown"
            isOpen={isConVoxOpen}
            toggle={() => setIsConVoxOpen(!isConVoxOpen)}>
            <DropdownToggle className="break-button" caret size="sm">
              {user?.process_details?.name}
            </DropdownToggle>
            <DropdownMenu>{user?.process_details?.name}</DropdownMenu>
          </ButtonDropdown>
        </div>
        <div className="px-4 py-2">{renderCallStatus()}</div>
        {renderScreen()}
        {renderBreakStatus()}
      </div>
    </div>
  );
};

export default CallingSidebar;
